/**
 * Quando recarregar a página depois de um deploy.
 *
 * O problema: os assets do build levam hash no nome e são servidos como
 * `immutable`, então depois de um deploy os arquivos antigos SOMEM do servidor.
 * Quem está com a aba aberta há horas — atendimento, agenda e funil ficam
 * abertos o dia inteiro — não só vê a tela velha: ao navegar para uma tela
 * carregada sob demanda recebe erro de import e tela branca. Pedir "dá um F5 no
 * grupo" é o que vinha mascarando isso.
 *
 * O que NÃO é possível: trocar o código com a página viva. Isso é HMR, e não sai
 * do ambiente de desenvolvimento. Todo site grande recarrega — a diferença é
 * recarregar no momento em que o usuário não perde nada.
 *
 * A regra abaixo é uma função pura para poder ser testada sem navegador: recebe
 * o estado (versões, visibilidade, se há texto não enviado) e devolve o que
 * fazer.
 */

/** O que o app deve fazer diante de uma versão nova no servidor. */
export type VersionAction =
    /** Nada a fazer: mesma versão, ou versão desconhecida. */
    | { kind: 'none' }
    /** Recarrega sozinho. `delayMs` espalha a rajada entre os clientes. */
    | { kind: 'reload'; delayMs: number }
    /** Avisa e espera o usuário: ele está no meio de alguma coisa. */
    | { kind: 'notify' }

export interface VersionState {
    /** Versão que este navegador carregou (`__APP_BUILD_ID__`). */
    current: string
    /** Versão que o servidor está servindo agora (`version.json`). */
    server: string | null
    /** A aba está à vista? Recarregar aba escondida não custa nada a ninguém. */
    visible: boolean
    /**
     * Há trabalho em andamento que um reload destruiria: mensagem digitada e não
     * enviada, formulário sujo. Na dúvida, o chamador manda `true`.
     */
    hasUnsavedWork: boolean
    /** Já recarregamos por causa DESTA versão? Trava contra laço. */
    alreadyReloadedFor: (version: string) => boolean
    /** Sorteio do atraso, injetável para o teste ser determinístico. */
    random?: () => number
}

/**
 * Atraso máximo do recarregamento automático.
 *
 * Existe por causa do rebanho: sem ele, todo mundo online recarrega no mesmo
 * segundo e a rajada bate de uma vez no nginx e na API — logo depois de um
 * deploy, que é quando o sistema está mais frágil.
 */
export const RELOAD_JITTER_MS = 20_000

export function decideVersionAction(state: VersionState): VersionAction {
    const { current, server } = state

    // Em desenvolvimento a versão é sempre `dev`: nunca dispara.
    if (!server || !current || server === current) return { kind: 'none' }

    // Rolling deploy: por alguns segundos uma réplica serve a versão nova e
    // outra a velha. Sem esta trava o cliente recarrega, cai na velha, recarrega
    // de novo — laço infinito na cara do usuário.
    if (state.alreadyReloadedFor(server)) return { kind: 'none' }

    // Aba escondida: ninguém está olhando, recarrega e pronto. É daqui que vem a
    // sensação de "atualizou sozinho".
    if (!state.visible) return { kind: 'reload', delayMs: 0 }

    // Aba à vista com trabalho em andamento: avisa e deixa a pessoa decidir.
    // Perder uma mensagem meio digitada é muito pior do que passar dez minutos
    // numa versão velha.
    if (state.hasUnsavedWork) return { kind: 'notify' }

    const random = state.random ?? Math.random
    return { kind: 'reload', delayMs: Math.floor(random() * RELOAD_JITTER_MS) }
}

/** Chave da trava por versão (sessionStorage: some ao fechar a aba). */
export function reloadGuardKey(version: string): string {
    return `app-version:reloaded:${version}`
}

/** Quando esta versão do servidor foi vista pela primeira vez. */
export interface VersionSighting {
    version: string | null
    at: number
}

/**
 * A versão nova se FIRMOU, ou o deploy ainda está em curso?
 *
 * O deploy é escalonado: por alguns minutos as VMs servem versões diferentes. Nessa
 * janela nada deve acontecer — nem recarregar, nem **avisar**, porque a tarja é o
 * convite para o usuário recarregar na mão, e no meio do rodízio ele cai na VM que
 * ainda não subiu ou numa que está reiniciando.
 *
 * Duas transições importam:
 *
 * - **versão nova vista pela primeira vez** → anota e espera (`confirmed: false`);
 * - **o servidor volta a responder a versão ATUAL** → o rodízio não acabou, e o
 *   relógio reinicia. Sem isso, a versão nova vista uma vez antes do fim do deploy
 *   seria dada como estável assim que reaparecesse, por mais que estivesse piscando
 *   entre as VMs.
 */
export function trackVersionSighting(
    previous: VersionSighting,
    server: string | null,
    current: string,
    now: number,
    stableAfterMs: number,
): { sighting: VersionSighting; confirmed: boolean } {
    // Sem resposta (servidor reiniciando): mantém o que já se sabia.
    if (!server) return { sighting: previous, confirmed: false }

    if (server === current) {
        return { sighting: { version: null, at: 0 }, confirmed: false }
    }

    if (previous.version !== server) {
        return { sighting: { version: server, at: now }, confirmed: false }
    }

    return { sighting: previous, confirmed: now - previous.at >= stableAfterMs }
}
